import { Source } from '@core/models/imageAssets';
import type {
  AnalyticsFilterOptions,
  Graph,
  MultiSeasonDateRange,
  SnapshotState,
  TimeSeriesState,
} from '@state/analytics/analytics.models';
import dayjs from 'dayjs';
import { create } from 'zustand';
import { DATE_RANGES_COLORS } from './analytics.constants';

interface AnalyticsState {
  filterOptions: AnalyticsFilterOptions;
  snapshot: SnapshotState;
  timeSeries: TimeSeriesState;
}

const initialState: AnalyticsState = {
  filterOptions: {
    source: Source.SENTINEL,
    cloudCoverage: 0.5,
    dateRange: [dayjs().subtract(6, 'month').toDate(), dayjs().toDate()],
  },
  snapshot: {
    selectedGraphs: [],
  },
  timeSeries: {
    sortMethod: 'both',
    dateRanges: [
      {
        dateRange: [dayjs().subtract(6, 'month').toDate(), dayjs().toDate()],
        color: DATE_RANGES_COLORS[0],
      },
    ],
    showPrecipitation: true,
  },
};

interface AnalyticsActions {
  setFilterOptions: (newOptions: Partial<AnalyticsFilterOptions>) => void;
  setSnapshotSelectedGraphs: (graphs: Graph[]) => void;
  setSnapshotSelectedGraphsIndexValue: (graph: Graph, selectedIndex: number) => void;
  setAnalyticsDateRange: (newDateRange: [Date, Date]) => void;
  addTimeSeriesDateRange: (newDateRange: [Date, Date]) => void;
  removeTimeSeriesDateRange: (targetColor: string) => void;
  setTimeSeriesDateRange: (newDateRange: [Date, Date], targetColor: string) => void;
  setShowPrecipitation: (showPrecipitation: boolean) => void;
  setPrecipitationRange: (color: MultiSeasonDateRange['color']) => void;
}

export const useAnalyticsStore = create<AnalyticsState & AnalyticsActions>((set) => ({
  ...initialState,
  setFilterOptions: (newOptions) =>
    set((state) => ({ filterOptions: { ...state.filterOptions, ...newOptions } })),
  setSnapshotSelectedGraphs: (graphs) =>
    set((state) => ({ snapshot: { ...state.snapshot, selectedGraphs: graphs } })),
  setSnapshotSelectedGraphsIndexValue: (graph, selectedIndex) =>
    set((state) => ({
      snapshot: {
        ...state.snapshot,
        selectedGraphs: state.snapshot.selectedGraphs
          .map((selected, index) => (index === selectedIndex ? graph : selected))
          .toSorted((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
      },
    })),
  setAnalyticsDateRange: (newDateRange) =>
    set((state) => ({
      filterOptions: {
        ...state.filterOptions,
        dateRange: newDateRange,
      },
    })),
  addTimeSeriesDateRange: (newDateRange) => {
    set((state) => {
      const currentRanges = state.timeSeries.dateRanges;
      const color = DATE_RANGES_COLORS.find(
        (color) => !currentRanges.some((r) => r.color === color)
      );

      if (color) {
        return {
          timeSeries: {
            ...state.timeSeries,
            dateRanges: [...state.timeSeries.dateRanges, { dateRange: newDateRange, color }],
          },
        };
      }

      return state;
    });
  },
  removeTimeSeriesDateRange: (targetColor?: string) => {
    set((state) => {
      const lasItemIndex = state.timeSeries.dateRanges.length - 1;
      const target = targetColor ?? state.timeSeries.dateRanges[lasItemIndex]?.color;

      const precipitationRangeColor =
        state.timeSeries.precipitationRangeColor === target
          ? undefined
          : state.timeSeries.precipitationRangeColor;

      return {
        timeSeries: {
          ...state.timeSeries,
          dateRanges: state.timeSeries.dateRanges.filter((range) => range.color !== target),
          precipitationRangeColor,
        },
      };
    });
  },
  setShowPrecipitation: (showPrecipitation) =>
    set((state) => ({ timeSeries: { ...state.timeSeries, showPrecipitation } })),
  setPrecipitationRange: (precipitationRangeColor) =>
    set((state) => ({ timeSeries: { ...state.timeSeries, precipitationRangeColor } })),
  setTimeSeriesDateRange: (newDateRange, targetColor) => {
    set((state) => {
      return {
        timeSeries: {
          ...state.timeSeries,
          dateRanges: state.timeSeries.dateRanges.map((range) => {
            if (range.color === targetColor) {
              return { color: range.color, dateRange: newDateRange };
            }
            return range;
          }),
        },
      };
    });
  },
}));
